import { Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlexibleStepperContextProps } from 'src/components/FlexibleStepper/types';
import LoaderOverlay from 'src/components/Loader';
import AuthStepperFooter from 'src/features/auth/presentation/components/AuthStepperFooter/AuthStepperFooter';
import AnimatedStepWrapper from 'src/features/auth/presentation/components/SignUpStepper/AnimatedStepWrapper';
import DocumentVerificationStep from 'src/features/auth/presentation/components/VerificationStepper/DocumentVerificationStep';
import useAuthMutations from 'src/features/auth/presentation/hooks/useAuthMutations';
import { IdVerificationStatus } from 'src/features/verification/data/service/idVerificationService.types';
import useIdVerificationStatus from 'src/features/verification/presentation/hooks/useIdVerificationStatus';

interface IdVerificationStatusStepProps {
  onSuccess?: (stepperContext: FlexibleStepperContextProps) => void;
}

export default function IdVerificationStatusStep(
  _props: IdVerificationStatusStepProps
) {
  const [userHasFinishedVerification, setUserHasFinishedVerification] =
    useState(false);
  const queryClient = useQueryClient();
  const { logoutMutation } = useAuthMutations();
  const {
    data: verificationSession,
    refetch,
    isLoading,
  } = useIdVerificationStatus();
  const navigate = useNavigate();

  const handleFinishedVerification = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['currentUser'] }).then(() => {
      navigate('/');
    });
  }, [queryClient, navigate]);

  useEffect(() => {
    if (verificationSession?.status === IdVerificationStatus.APPROVED) {
      handleFinishedVerification();
    }
  }, [handleFinishedVerification, verificationSession?.status]);

  useEffect(() => {
    if (userHasFinishedVerification) {
      refetch();
    }
  }, [userHasFinishedVerification, refetch]);

  if (!verificationSession && isLoading) {
    return <LoaderOverlay />;
  }

  if (!verificationSession && !userHasFinishedVerification) {
    return (
      <DocumentVerificationStep
        onFinishedVerification={() => setUserHasFinishedVerification(true)}
      />
    );
  }

  const handleRestartVerification = () => {
    setUserHasFinishedVerification(false);
  };

  const handleRefresh = () => {
    refetch();
  };

  const labels = {
    [IdVerificationStatus.IN_REVIEW]: {
      title: 'Estamos verificando tu información',
      description: 'Este proceso puede tardar unos minutos. Por favor espera',
      handler: handleRefresh,
      actionLabel: 'Refrescar',
    },
    [IdVerificationStatus.APPROVED]: {
      title: '¡Listo! Ya puedes continuar',
      description:
        'Tu identidad ha sido verificada con éxito. Te redireccionaremos en breve',
      handler: handleFinishedVerification,
      actionLabel: 'Continuar',
    },
    [IdVerificationStatus.DECLINED]: {
      title: '¡Lo sentimos!',
      description:
        'No pudimos verificar tu identidad. Por favor intenta de nuevo',
      handler: handleRestartVerification,
      actionLabel: 'Reintentar',
    },
    [IdVerificationStatus.ABANDONED]: {
      title: '¡Lo sentimos!',
      description:
        'No pudimos verificar tu identidad. Por favor intenta de nuevo',
      handler: handleRestartVerification,
      actionLabel: 'Reintentar',
    },
    [IdVerificationStatus.EXPIRED]: {
      title: '¡Lo sentimos!',
      description:
        'No pudimos verificar tu identidad. Por favor intenta de nuevo',
      handler: handleRestartVerification,
      actionLabel: 'Reintentar',
    },
    [IdVerificationStatus.IN_PROGRESS]: {
      title: 'Aun no has completado tu verificación',
      description: 'Te redireccionaremos a la página de verificación',
      handler: handleRestartVerification,
      actionLabel: 'Continuar',
    },
    [IdVerificationStatus.KYC_EXPIRED]: {
      title: '¡Lo sentimos!',
      description:
        'No pudimos verificar tu identidad. Por favor intenta de nuevo',
      handler: handleRestartVerification,
      actionLabel: 'Reintentar',
    },
    [IdVerificationStatus.NOT_STARTED]: {
      title: 'Aun no has completado tu verificación',
      description: 'Te redireccionaremos a la página de verificación',
      handler: handleRestartVerification,
      actionLabel: 'Continuar',
    },
  };

  const title = verificationSession
    ? labels[verificationSession.status].title
    : '';
  const description = verificationSession
    ? labels[verificationSession.status].description
    : '';
  const handler = verificationSession
    ? labels[verificationSession.status].handler
    : () => {};
  const actionLabel = verificationSession
    ? labels[verificationSession.status].actionLabel
    : '';

  const backButtonLabel = 'Cerrar sesión';
  const handleSupportClick = () => {};

  const handleLogout = async () => {
    logoutMutation.mutate();
  };

  return (
    <AnimatedStepWrapper>
      <Typography
        variant="h5"
        textAlign="center"
        fontWeight="fontWeightMedium"
        gutterBottom
      >
        {title}
      </Typography>
      <Typography
        textAlign="left"
        variant="body1"
        fontWeight="fontWeightRegular"
      >
        {description}
      </Typography>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        disabled={isLoading}
        onClick={handler}
        sx={{ marginTop: 'auto' }}
      >
        {actionLabel}
      </Button>
      <AuthStepperFooter
        backButton={{
          color: 'greyText',
          label: backButtonLabel,
          onClick: handleLogout,
          variant: 'text',
        }}
        nextButton={{
          color: 'error',
          label: 'Ayuda',
          onClick: handleSupportClick,
          variant: 'text',
        }}
      />
    </AnimatedStepWrapper>
  );
}
